import React from 'react';
import { ContentArea } from '../TermsAndConditions/styles';

type Props = { className?: string };

const PrivacyPolicy = ({ className }: Props) => (
  <ContentArea className={className}>
    <h1>Wi-Q Platform Privacy Notice </h1>
    <p>Updated: February 2025</p>

    <h3>1. Introduction</h3>
    <p>
      wi-Q is a cloud-based mobile ordering platform (Platform) that allows you to: (i) access a trader’s
      menu; (ii) order food and drinks; and (iii) pay for your order using a selection of payment methods, all
      using your mobile device, without the need to download an app.
    </p>
    <p>
      We are committed to protecting your personal data and maintaining transparency in how we collect,
      process, and store your information when you use our Platform. This Privacy Policy outlines how we
      handle your data in compliance with the UK General Data Protection Regulation (UK GDPR) and the EU
      General Data Protection Regulation (EU GDPR).
    </p>

    <h3>2. Data Controller</h3>
    <p>
      wi-Q Limited (Company Number: 09209151), registered at One Silk St, Ancoats, Manchester M4 6LZ, is the
      Data Controller responsible for processing your personal data when you access and use our Platform.
    </p>

    <h3>3. What Personal Data We Collect</h3>
    <p>
      When you use the Platform to access a trader’s menu, place an order, and make a payment, we may collect
      and process the following personal data:
    </p>
    <ul>
      <li>
        <strong>Identity Data:</strong> Name, contact details (e.g., email address, phone number, delivery
        address), and any other identifying information provided.
      </li>
      <li>
        <strong>Order Information:</strong> Details of the items ordered, order preferences, and transaction
        history.
      </li>
      <li>
        <strong>Payment Data:</strong> Payment method used, last four digits of card details (if applicable),
        and transaction details processed by third-party payment providers.
      </li>
      <li>
        <strong>Device and Usage Data:</strong> IP address, device type, operating system, browser type,
        session duration, tracking cookies, and interaction with the Platform.
      </li>
      <li>
        <strong>Location Data:</strong> Approximate geolocation (latitude and longitude) to support order
        fulfilment.
      </li>
      <li>
        <strong>Customer Preferences:</strong> Allergen information and order notes.
      </li>
      <li>
        <strong>Financial Information:</strong> Client card payment details (redacted PAN and token).
      </li>
      <li>
        <strong>Customer Room Information:</strong> Room number (if applicable).
      </li>
      <li>
        <strong>Date of Birth:</strong> If provided by the customer.
      </li>
    </ul>

    <h3>4. How We Use Your Personal Data</h3>
    <p>We process your personal data for the following purposes: </p>
    <ul>
      <li>
        To provide access to the Platform and enable you to browse a trader’s menu, place orders, inform the
        venue of any allergy requirements and make payments.
      </li>
      <li>To facilitate secure payment processing via third-party payment providers. </li>
      <li>To personalise user experience and improve Platform functionality. </li>
      <li>To manage customer support inquiries and handle disputes or refund requests. </li>
      <li>To comply with legal obligations, including fraud prevention and security monitoring. </li>
      <li>To analyse user interactions with the Platform to enhance service performance and reliability. </li>
    </ul>

    <h3>5. Legal Basis for Processing Personal Data</h3>
    <p>We process your personal data based on the following legal grounds:</p>
    <ul>
      <li>
        <strong>Performance of a Contract:</strong> To fulfil your order and process payments.
      </li>
      <li>
        <strong>Legitimate Interests:</strong> To improve our Platform, prevent fraud, and ensure the security
        of our services.
      </li>
      <li>
        <strong>Consent:</strong> When you provide explicit consent for optional data processing activities
        (e.g., marketing communications).
      </li>
      <li>
        <strong>Legal Obligation:</strong> To comply with applicable legal and regulatory requirements.
      </li>
      <li>
        <strong>Vital Interests:</strong> To protect your health by processing allergen information, helping
        to prevent adverse reactions and ensuring your dietary needs are accommodated.
      </li>
    </ul>

    <h3>6. Data Sharing and Third-Party Processors</h3>
    <p>
      We do not sell or rent your personal data. However, we may share your data with the following third
      parties:
    </p>
    <ul>
      <li>
        <strong>Traders:</strong> To facilitate order processing and fulfilment.
      </li>
      <li>
        <strong>Payment Providers:</strong> To process transactions securely (e.g., Go Cardless, Stripe).
      </li>
      <li>
        <strong>Cloud Hosting and IT Service Providers:</strong> To support Platform operations (e.g.,
        Microsoft 365, Google Cloud, SaaS platforms).
      </li>
      <li>
        <strong>Regulatory Authorities:</strong> When required by law or to enforce our legal rights.
      </li>
    </ul>
    <p>
      All third-party service providers are contractually bound to ensure the confidentiality and security of
      your personal data in compliance with GDPR.
    </p>

    <h3>7. Data Retention</h3>
    <p>
      We retain personal data only for as long as necessary to fulfil the purposes outlined in this Privacy
      Policy:
    </p>
    <ul>
      <li>
        <strong>Order and transaction data:</strong> Retained for up to 7 (6+1) years for tax and accounting
        purposes.
      </li>
      <li>
        <strong>Device and usage data:</strong> Retained for up to 14 months for analytics and security
        monitoring.
      </li>
    </ul>
    <p>Once the retention period expires, personal data is securely deleted or anonymised.</p>

    <h3>8. Your Rights Under GDPR</h3>
    <p>Under GDPR, you have the following rights regarding your personal data:</p>
    <ul>
      <li>
        <strong>Right to Access:</strong> Request a copy of the personal data we hold about you.
      </li>
      <li>
        <strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data.
      </li>
      <li>
        <strong>Right to Erasure:</strong> Request deletion of your data when no longer necessary.
      </li>
      <li>
        <strong>Right to Restrict Processing:</strong> Request temporary suspension of data processing.
      </li>
      <li>
        <strong>Right to Object:</strong> Object to data processing based on legitimate interests.
      </li>
      <li>
        <strong>Right to Withdraw Consent:</strong> Withdraw consent for marketing communications at any time.
      </li>
    </ul>
    <p>
      To exercise your rights, contact us at <a href="mailto:info@wi-q.com">info@wi-q.com</a>. We will respond
      within one month in accordance with GDPR regulations.
    </p>

    <h3>9. Data Security</h3>
    <p>
      We implement appropriate technical and organisational measures to safeguard personal data against
      unauthorised access, alteration, or loss. These measures include encryption, access controls, and
      regular security assessments.
    </p>

    <h3>10. International Data Transfers</h3>
    <p>
      If personal data is transferred outside the UK or EEA, we ensure appropriate safeguards are in place,
      such as Standard Contractual Clauses (SCCs) or equivalent measures to protect your data.
    </p>

    <h3>11. Cookies and Tracking Technologies</h3>
    <p>
      We use cookies and similar technologies to enhance user experience and analyse Platform usage. For more
      details, please refer to our{' '}
      <a target="_blank" href="https://www.wi-q.com/cookiepolicy">
        Cookie Policy
      </a>
      .
    </p>

    <h3>12. Updates to This Privacy Policy</h3>
    <p>
      We may update this Privacy Policy periodically. The latest version will be posted on our Platform with
      the update date.
    </p>

    <h3>13. Contact Information</h3>
    <p>For privacy-related inquiries, please contact us at:</p>
    <address>
      wi-Q Technologies Limited
      <br />
      One Silk St, Ancoats, Manchester M4 6LZ
      <br />
      Email: <a href="mailto:info@wi-q.com">info@wi-q.com</a>
    </address>
    <p>
      If you are not satisfied with how we handle your data, you have the right to lodge a complaint with the
      UK Information Commissioner’s Office (ICO) or your local data protection authority.
    </p>
  </ContentArea>
);

export default PrivacyPolicy;
